import { ConnectionDetails, Datetime, createNotification, queryInput } from '@/utils';
import { ForwardRefExoticComponent, forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';

import EditModal from '@/components/EditModal/new';
import SimpleTable from '@/components/SimpleTable';
import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import SimpleTableRow from '@/components/SimpleTable/SimpleTableRow';
import { Toast } from '@/models';
import { Trip } from '@/models/gen/graphql';
import combineTrips from '@/api/services/trips/combineTrips';
import { createComponentState } from '@/state';
import { getClasses } from '@/utils/strings';
import updateTripBulk from '@/api/services/trips/updateTripBulk';
import { useSearchCombinableTrips } from '@/api/services/trips/searchCombinableTrips';

type EditCombineModalState = {
  show?: boolean;
  loading?: boolean;
  tripId?: string;
  combineId?: string;
  scheduled?: string;
  servicerIataAirlineCode?: string;
  flightNumber?: number;
  selected?: string[];
  initSelected?: string[];
  unCombined?: string[];
  rows?: Trip[];
  onSubmit?: (data: any) => Promise<void>;
  onHide?: () => void;
};
const initEditCombineModalState: EditCombineModalState = {
  show: false,
  loading: false,
  tripId: undefined,
  combineId: undefined,
  scheduled: undefined,
  servicerIataAirlineCode: undefined,
  flightNumber: undefined,
  selected: [],
  initSelected: [],
  unCombined: [],
  rows: [],
  onSubmit: undefined,
  onHide: undefined,
};

export const useEditCombineModal = createComponentState(initEditCombineModalState);

// TODO: Find/use the type generated in GraphQL.
type UncombineTripsPayload = { id: string; combineId: [null]; driverId: [null]; vehicleId: [null]; combineType: [null] };

const handleSubmitCombine = async ({ updates: { combineTripIds, unCombinePayload }, ...data }: any): Promise<void> => {
  let changesMade = false;
  if (combineTripIds.length > 0) {
    changesMade = true;
    await combineTrips({
      tripIds: combineTripIds,
      driverId: data.driverId,
      vehicleId: data.vehicleId,
    });
  }
  if (unCombinePayload.length > 0) {
    changesMade = true;
    const updateResponse = await updateTripBulk(unCombinePayload, false);
    let updated = 0;
    for (let i = 1; i <= (updateResponse || []).length; i++) {
      const node = updateResponse[i - 1];
      if (node.error) {
        return createNotification(node.error.message || 'Failed to uncombine trips', Toast.Type.DANGER, 'Uncombine Trips');
      }
      updated += node.updated || 0;
      if (i === updateResponse.length && updated > 0) return createNotification('Success', Toast.Type.SUCCESS, 'Uncombine Trips');
    }
  }
  if (!changesMade) return createNotification('No changes were made.', Toast.Type.WARNING, 'Combine Trips');
};

const EditCombineModal = (): JSX.Element => {
  const [state, setState] = useEditCombineModal(({ state, setState }) => [state, setState]);
  const {
    show = false,
    tripId = '',
    combineId = '',
    selected = [],
    initSelected = [],
    unCombined = [],
    onSubmit = async () => undefined,
    onHide = () => setState(initEditCombineModalState),
  } = state;
  const [{ data: combineResults = {} as ConnectionDetails<Trip> }, { fetch: searchCombinableTrips, refetch: refetchCombinableTrips }] =
    useSearchCombinableTrips();
  const { rows = [], totalCount = 0 } = combineResults;
  const lastTripId = useRef(null);

  const handleSubmit = useCallback(async (): Promise<void> => {
    const combineTripIds = selected?.filter?.((id: string): boolean => !initSelected.includes(id)) || [];
    if (combineTripIds.length > 0) combineTripIds.push(tripId);
    const unCombinePayload =
      unCombined?.map?.(
        (id: string): UncombineTripsPayload => ({
          id,
          combineId: [null],
          driverId: [null],
          vehicleId: [null],
          combineType: [null],
        })
      ) || [];
    const payload = { updates: { combineTripIds, unCombinePayload }, ...state };
    await handleSubmitCombine(payload);
    onSubmit?.(state);
    setState(initEditCombineModalState);
  }, [state, tripId, handleSubmitCombine, onSubmit, setState, initSelected]);

  const getCombinableTrips = useCallback(async (): Promise<void> => {
    const fn = lastTripId.current === tripId ? refetchCombinableTrips : searchCombinableTrips;
    lastTripId.current = tripId;
    const response = await fn([{ id: queryInput(tripId) }]);
    const selected = ((response?.rows || [])?.filter((node: Trip): boolean => (node.combineId || '') === combineId) || []).map(
      (node: Trip): string => node.id
    );
    setState(
      (current: EditCombineModalState): EditCombineModalState => ({
        ...current,
        selected,
        initSelected: selected,
        rows: response.rows,
      })
    );
  }, [tripId, combineId, searchCombinableTrips, refetchCombinableTrips, setState]);

  useEffect((): void => {
    if (!tripId) return;
    getCombinableTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const scheduled = state?.scheduled ? new Datetime(state?.scheduled).frontendDatetimeShort : '';
  const airlineCode = (state?.servicerIataAirlineCode || '').toUpperCase();
  const flightNumber = state?.flightNumber ? state?.flightNumber.toString().padStart(4, '0') : '';
  const title = `Combine Schedule - ${airlineCode}${flightNumber} ${scheduled}`;
  const icon = 'sv sv-combine';
  const options = {
    footer: {
      submitButtonText: 'Confirm',
    },
  };

  return (
    <EditModal
      className="editCombineModal"
      show={!!show}
      title={title}
      icon={icon}
      options={options}
      onHide={onHide}
      onSubmit={handleSubmit}
    >
      <SimpleTable
        className="CombineTripsTable"
        rows={rows.map(({ id }) => id)}
        columns={CombineTripsTableColumns}
        row={CombineTripsTableRow}
      />
      <div className="mt-3">
        <span>
          {selected.length || 0} of {totalCount || 0} trips selected
        </span>
      </div>
    </EditModal>
  );
};

const CombineTripsTableColumns = (): JSX.Element => {
  return (
    <SimpleTableRow header>
      <SelectAllCell />
      <SimpleTableCell name="FLT">FLT</SimpleTableCell>
      <SimpleTableCell name="TIME">TIME</SimpleTableCell>
      <SimpleTableCell name="PLT">PLT</SimpleTableCell>
      <SimpleTableCell name="FA">FA</SimpleTableCell>
    </SimpleTableRow>
  );
};

const CombineTripsTableRow = forwardRef(({ id }: { id: string }, ref: ForwardRefExoticComponent<HTMLDivElement>): JSX.Element => {
  const [{ isSelected, trip }, setState] = useEditCombineModal(({ state: { selected, rows }, setState }) => {
    return [
      {
        isSelected: selected?.includes?.(id),
        trip: rows.find((t) => t.id === id) || ({} as Trip),
      },
      setState,
    ];
  });

  const onSelect = useCallback(
    (): void =>
      setState((current) => {
        let newSelected = current.selected;
        const unCombined = [];
        if (newSelected.find((tripId) => tripId === id)) {
          newSelected = newSelected.filter((tripId) => tripId !== id);
          unCombined.push(id);
        } else {
          newSelected = [...newSelected, id];
        }
        return { ...current, selected: newSelected, unCombined };
      }),
    [trip, id, setState]
  );

  return (
    <SimpleTableRow id={id} className={getClasses(isSelected ? 'SELECTED' : undefined)} ref={ref} visible>
      <SimpleTableCell.Select checked={isSelected} onChange={onSelect} />
      <SimpleTableCell name="FLT">{trip.flightNumber}</SimpleTableCell>
      <SimpleTableCell name="TIME">{new Datetime(trip.scheduled).time}</SimpleTableCell>
      <SimpleTableCell name="PLT">{trip.pilots}</SimpleTableCell>
      <SimpleTableCell name="FA">{trip.attendants}</SimpleTableCell>
    </SimpleTableRow>
  );
});

const SelectAllCell = (): JSX.Element => {
  const [{ selected, rows }, setState] = useEditCombineModal(({ state: { selected, rows }, setState }) => [
    { selected, rows: rows.map(({ id }) => id) },
    setState,
  ]);

  const isSelectAll = useMemo((): boolean => {
    if (selected.length !== rows.length) return false;
    for (const id of selected) {
      if (!rows.includes(id)) return false;
    }
    return true;
  }, [rows, selected]);

  const onClick = useCallback(
    (): void => setState((current) => ({ ...current, selected: isSelectAll ? [] : rows })),
    [rows, isSelectAll, setState]
  );

  return <SimpleTableCell.Select checked={isSelectAll} onChange={onClick} />;
};

export default EditCombineModal;
